import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router';
import { useNotification } from './CustomNotification';
import {
  getOne as getEmployee,
  updateOne as updateEmployee,
  uploadContractImage,
  uploadProfileImage,
  validate as validateEmployee,
  type Employee,
} from '../data/employees';
import { fetchImageAsFile } from '../services/api';
import EmployeeForm, {
  type FormFieldValue,
  type EmployeeFormState,
} from './EmployeeForm';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';

function EmployeeEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<EmployeeFormState['values']>;
  onSubmit: (formValues: Partial<EmployeeFormState['values']>) => Promise<void>;
}) {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const notifications = useNotification();

  const [formState, setFormState] = React.useState<EmployeeFormState>(() => ({
    values: initialValues,
    errors: {},
  }));
  const formValues = formState.values;
  const formErrors = formState.errors;

  // Store initial values for comparison
  const initialValuesRef = React.useRef(initialValues);

  const setFormValues = React.useCallback(
    (newFormValues: Partial<EmployeeFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<EmployeeFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof EmployeeFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<EmployeeFormState['values']>) => {
        const { issues } = validateEmployee(values, false);
        const errorMessage = issues?.find((issue) => issue.path?.[0] === name)?.message;
        setFormErrors({
          ...formErrors,
          [name]: errorMessage ? t(errorMessage) : undefined,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues, t],
  );

  const handleFormReset = React.useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = React.useCallback(async () => {
    const { issues } = validateEmployee(formValues, false);
    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], t(issue.message)])),
      );
      return;
    }
    setFormErrors({});

    // Check if there are any changes
    const hasDataChanges =
      formValues.name !== initialValuesRef.current.name ||
      formValues.email !== initialValuesRef.current.email ||
      formValues.branch_id !== initialValuesRef.current.branch_id ||
      formValues.role_name !== initialValuesRef.current.role_name ||
      formValues.employment_status !== initialValuesRef.current.employment_status ||
      formValues.contract_type !== initialValuesRef.current.contract_type ||
      formValues.salary !== initialValuesRef.current.salary ||
      formValues.available_leave !== initialValuesRef.current.available_leave ||
      formValues.password !== initialValuesRef.current.password;

    const hasNewContractImage = formValues.contract_image instanceof File;
    const hasContractImageChanges =
      formValues.contract_image_url !== initialValuesRef.current.contract_image_url;

    const hasNewProfileImage = formValues.profile_image instanceof File;
    const hasProfileImageChanges =
      formValues.profile_image_url !== initialValuesRef.current.profile_image_url;

    if (!hasDataChanges && !hasNewContractImage && !hasContractImageChanges && !hasNewProfileImage && !hasProfileImageChanges) {
      notifications.show(t('messages.noChanges'), {
        severity: 'info',
        autoHideDuration: 3000,
      });
      return;
    }

    try {
      await onSubmit(formValues);
      notifications.show(t('messages.updateSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      navigate(`/employees/${employeeId}`);
    } catch (editError) {
      notifications.show(
        `${t('messages.errorOccurred')}: ${(editError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw editError;
    }
  }, [formValues, navigate, notifications, onSubmit, setFormErrors, t]);

  return (
    <EmployeeForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel={t('common.save')}
      backButtonPath={`/employees/${employeeId}`}
    />
  );
}

export default function EmployeeEdit() {
  const { employeeId } = useParams();
  const { t } = useLanguage();

  const [employee, setEmployee] = React.useState<Employee | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const showData = await getEmployee(Number(employeeId));

      setEmployee(showData);
    } catch (showDataError) {
      setError(showDataError as Error);
    }
    setIsLoading(false);
  }, [employeeId]);

  const initialValues = React.useMemo(() => {
    if (!employee) return {};

    return {
      ...employee,
      contract_image_url: employee.contract_photo_path || undefined,
      profile_image_url: employee.profile_photo_path || undefined,
      role: employee.role_name
    };
  }, [employee]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = React.useCallback(
    async (formValues: Partial<EmployeeFormState['values']>) => {
      // Step 1: Update employee data
      var employeeData: any = {
        name: formValues.name,
        email: formValues.email,
        branch_id: formValues.branch_id,
        role_name: formValues.role_name,
        employment_status: formValues.employment_status,
        contract_type: formValues.contract_type,
        salary: formValues.salary,
        available_leave: formValues.available_leave,
      }

      // Only include password if it's provided
      if (formValues.password && formValues.password.trim() !== '') {
        employeeData.password = formValues.password;
      }

      const updatedData = await updateEmployee(Number(employeeId), employeeData);

      // Step 2: Handle profile image upload
      const newProfileImage = formValues.profile_image as File || undefined;
      const existingProfileUrl = formValues.profile_image_url;

      // If there's a new profile image, upload it
      if (newProfileImage) {
        await uploadProfileImage(Number(employeeId), newProfileImage);
      }
      // If there's an existing profile URL but no new image, re-upload the existing one
      else if (existingProfileUrl) {
        try {
          const existingImageFile = await fetchImageAsFile(existingProfileUrl);
          await uploadProfileImage(Number(employeeId), existingImageFile);
        } catch (error) {
          console.error('Failed to fetch existing profile image:', existingProfileUrl, error);
          throw new Error(`Failed to download existing profile image: ${existingProfileUrl}`);
        }
      }

      // Step 3: Handle contract image upload
      const newContractImage = formValues.contract_image as File || undefined;
      const existingContractUrl = formValues.contract_image_url;

      // If there's a new contract image, upload it
      if (newContractImage) {
        await uploadContractImage(Number(employeeId), newContractImage);
      }
      // If there's an existing contract URL but no new image, re-upload the existing one
      else if (existingContractUrl) {
        try {
          const existingImageFile = await fetchImageAsFile(existingContractUrl);
          await uploadContractImage(Number(employeeId), existingImageFile);
        } catch (error) {
          console.error('Failed to fetch existing contract image:', existingContractUrl, error);
          throw new Error(`Failed to download existing contract image: ${existingContractUrl}`);
        }
      }

      setEmployee(updatedData);
    },
    [employeeId],
  );

  const renderEdit = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return employee ? (
      <EmployeeEditForm initialValues={initialValues} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, employee, initialValues, handleSubmit]);

  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const navigate = useNavigate();

  const handleBack = React.useCallback(() => {
    navigate(`/employees/${employeeId}`);
  }, [navigate, employeeId]);

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('employees.title'), path: '/employees' },
        { title: `${t('employees.view')}`, path: `/employees/${employeeId}` },
        { title: t('employees.edit') },
      ]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={(theme) => ({
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : '#31394a',
              color: theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : 'white'
            },
          })}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
